import { useState } from "react";

import ScanResultsBrowser, {
  describeSelection,
  SelectedWafers,
} from "views/pages/ScanResultsPage/ScanResultsBrowser";
import { ScanExportStatus } from "views/pages/ScanResultsPage/types";
import { TargetDataset } from "views/pages/ScanResultsPage/targetDataset";
import AddDataModal from "./AddDataModal";
import styles from "./ScanImport.module.scss";

interface ScanResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  exportStatus: Record<string, ScanExportStatus>;
  /** The dataset media is being added to; see ScanResultsBrowser. */
  target?: TargetDataset | null;
  targetPending?: boolean;
  onAdd: (
    entries: { srPath: string; name: string; waferPaths: string[] }[],
  ) => void;
}

/**
 * Browse DataServer, tick scan results or single wafers, and "Add" hands them
 * to the export queue and closes. Rows already on their way in are locked, so nothing
 * goes twice.
 */
export default function ScanResultsModal({
  isOpen,
  onClose,
  exportStatus,
  target,
  targetPending,
  onAdd,
}: ScanResultsModalProps) {
  const [selectedWafers, setSelectedWafers] = useState<SelectedWafers>({});
  const [names, setNames] = useState<Record<string, string>>({});
  const selection = describeSelection(selectedWafers);

  const close = () => {
    setSelectedWafers({});
    onClose();
  };

  const handleAdd = () => {
    onAdd(
      Object.entries(selectedWafers).map(([srPath, waferPaths]) => ({
        srPath,
        name: names[srPath] ?? srPath,
        waferPaths,
      })),
    );
    close();
  };

  return (
    <AddDataModal
      isOpen={isOpen}
      onClose={close}
      title="Import from Scan Result"
      subTitle="Pick scan results or single wafers. They are exported in the background and appear in the folder table as they land."
      footerInfo={
        selection ? (
          <>
            <span>{selection}</span>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setSelectedWafers({})}
            >
              Clear
            </button>
          </>
        ) : (
          "Nothing selected yet"
        )
      }
      addDisabledReason={selection ? "" : "Select at least one scan result"}
      onAdd={handleAdd}
      fixedHeight
    >
      <div className={styles.browser}>
        <ScanResultsBrowser
          selectedWafers={selectedWafers}
          onSelectedWafersChange={setSelectedWafers}
          exportStatus={exportStatus}
          onNamesChange={setNames}
          target={target}
          targetPending={targetPending}
        />
      </div>
    </AddDataModal>
  );
}
